import React, { useEffect, useState } from "react";
import "./ServiceOverview.css";

import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ServiceOverview = ({ data }) => {
  // =====================================================
  // SLIDER STATE
  // =====================================================

  const [currentImage, setCurrentImage] = useState(0);

  // =====================================================
  // SAFE IMAGE DATA
  // =====================================================

  const overviewImages = Array.isArray(data?.overviewImages)
    ? data.overviewImages
    : [];

  // =====================================================
  // AUTO SLIDESHOW
  // Changes image every 5 seconds
  // =====================================================

  useEffect(() => {
    if (overviewImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentImage((previous) => {
        if (previous === overviewImages.length - 1) {
          return 0;
        }

        return previous + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [overviewImages.length]);

  // =====================================================
  // NEXT IMAGE
  // =====================================================

  const nextImage = () => {
    if (overviewImages.length <= 1) {
      return;
    }

    setCurrentImage((previous) => {
      if (previous === overviewImages.length - 1) {
        return 0;
      }

      return previous + 1;
    });
  };

  // =====================================================
  // PREVIOUS IMAGE
  // =====================================================

  const previousImage = () => {
    if (overviewImages.length <= 1) {
      return;
    }

    setCurrentImage((previous) => {
      if (previous === 0) {
        return overviewImages.length - 1;
      }

      return previous - 1;
    });
  };

  // =====================================================
  // IMAGE URL
  // Supports normal URLs and Cloudinary objects
  // =====================================================

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (typeof image === "string") {
      return image;
    }

    return image.url || image.secure_url || image.imageUrl || image.src || "";
  };

  // =====================================================
  // CURRENT IMAGE
  // =====================================================

  const activeImage =
    overviewImages.length > 0 ? getImageUrl(overviewImages[currentImage]) : "";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <section className="service-overview">
      <div className="service-overview-container">
        {/* =================================================
            LEFT SIDE
        ================================================= */}

        <div className="overview-content">
          {/* SECTION TAG */}

          <span className="section2-tag">ABOUT THIS SERVICE</span>

          {/* TITLE */}

          <h2>{data?.overview?.title}</h2>

          {/* DESCRIPTION */}

          <p>{data?.overview?.description}</p>

          {/* =================================================
              FOUR SPECIFICATION CARDS
          ================================================= */}

          <div className="overview-features">
            {Array.isArray(data?.benefits) &&
              data.benefits.slice(0, 4).map((item, index) => (
                <div className="overview-feature" key={index}>
                  {/* CHECK ICON */}

                  <div className="feature-icon">✓</div>

                  {/* CONTENT */}

                  <div className="overview-feature-content">
                    <h4>{item.title}</h4>

                    <span>{item.description}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* =================================================
            RIGHT SIDE - ONE LARGE IMAGE SLIDESHOW
        ================================================= */}

        <div className="overview-gallery">
          {activeImage ? (
            <div className="overview-slider">
              {/* IMAGE */}

              <img
                key={activeImage}
                src={activeImage}
                alt={data?.overview?.title || data?.title || "Service image"}
                className="overview-slider-image"
              />

              {/* DARK BOTTOM OVERLAY */}

              <div className="overview-slider-overlay"></div>

              {/* =================================================
                  PREVIOUS BUTTON
              ================================================= */}

              {overviewImages.length > 1 && (
                <button
                  type="button"
                  className="overview-slider-arrow overview-slider-prev"
                  onClick={previousImage}
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
              )}

              {/* =================================================
                  NEXT BUTTON
              ================================================= */}

              {overviewImages.length > 1 && (
                <button
                  type="button"
                  className="overview-slider-arrow overview-slider-next"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
              )}

              {/* =================================================
                  IMAGE COUNTER
              ================================================= */}

              {overviewImages.length > 1 && (
                <div className="overview-slider-counter">
                  <span>{currentImage + 1}</span>

                  <small>/</small>

                  <span>{overviewImages.length}</span>
                </div>
              )}

              {/* =================================================
                  DOTS
              ================================================= */}

              {overviewImages.length > 1 && (
                <div className="overview-slider-dots">
                  {overviewImages.map((_, index) => (
                    <button
                      type="button"
                      key={index}
                      className={index === currentImage ? "active" : ""}
                      onClick={() => setCurrentImage(index)}
                      aria-label={`Show image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* =================================================
               NO IMAGE
            ================================================= */

            <div className="overview-no-image">
              <span>No service images available</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceOverview;
